import {HttpClient, HttpParams} from '@angular/common/http';
import {inject, Injectable, signal} from '@angular/core';
import {GenNormalOutages} from 'frontend/generated/serverModels/GenNormalOutages';
import {ResponseWrapper} from '../classes/response-wrapper';
import {debounceTime, map, Observable, of, Subject, switchMap} from 'rxjs';
import {NormalOutageData} from '../modules/layer/classes/normal-outage-data';
import {schemeBlues} from 'd3-scale-chromatic';
import {ScaleLinear, ScaleQuantile} from 'd3';
import {DecimalPipe} from '@angular/common';

export interface GetNormalOutageDataArguments {
    level: string;
    start: Date;
    end: Date;
    state: string[] | undefined;
}

@Injectable({
    providedIn: 'root',
})
export class NormalDataService {
    private http = inject(HttpClient);
    private decimalPipe = inject(DecimalPipe);

    // Subject containing the arguments needed to get normal outage data
    private normalOutageArgs: Subject<{params: HttpParams; level: string}> = new Subject();

    // https://developers.arcgis.com/javascript/latest/visualization/symbols-color-ramps/esri-color-ramps/#ramps
    public colorScale = signal(
        // Esri color ramps - Blue 3
        ['#eff3ffff', '#bdd7e7ff', '#6baed6ff', '#3182bdff', '#08519cff']
    );
    public quantileScale: ScaleQuantile<string, never> | undefined;
    public linearScale: ScaleLinear<number, number, never>;

    // Instead of calling http.get() directly we listen to the changes in the signal.
    // That allows us to debounce it.
    public normalOutageData: Observable<NormalOutageData[]> = this.normalOutageArgs.pipe(
        debounceTime(700),
        switchMap((args) => {
            // The backend errors out if we dont send something for the state param
            // but thats ok. The base assumption is that if the user doesnt select any states
            // they dont get any data.
            if (!args.params.get('state')) {
                return of([]);
            }

            // There is a /utility endpoint but the /county?showByUtility=true return the desired data better
            if (args.level === 'utility') {
                args.level = 'county';
                args.params = args.params.set('showByUtility', true);
            }

            return this.http.get<ResponseWrapper<NormalOutageData>>(`/api/normal/${args.level}`, {params: args.params}).pipe(
                map((data) => {
                    return data.data;
                })
            );
        })
    );

    // Set the params on the subject when we want to update the data
    public getNormalOutageData(args: GetNormalOutageDataArguments): void {
        const params: HttpParams = new HttpParams()
            .set('start', args.start.toISOString())
            .set('end', args.end.toISOString())
            .set('state', args.state.toString());

        this.normalOutageArgs.next({
            level: args.level,
            params,
        });
    }

    public setScaleTools(quantileScale: ScaleQuantile<string, never>, linearScale: ScaleLinear<number, number, never>) {
        this.quantileScale = quantileScale;
        this.linearScale = linearScale;
    }

    // Returns the numeric range for a particular color. exe: "17 - 22.34"
    public colorRange(color: string): string {
        // Get the values that a color represents. Will be between 0 and 100
        const colorToQuantile = this.quantileScale?.invertExtent(color);

        if (colorToQuantile) {
            // Use those scaled values to find the original values. Eg: 15 -> 725
            const invertedValueLow = this.linearScale.invert(colorToQuantile[0]);
            const invertedValueHigh = this.linearScale.invert(colorToQuantile[1]);

            return `${this.decimalPipe.transform(invertedValueLow)} - ${this.decimalPipe.transform(invertedValueHigh)}`;
        }
        return '';
    }
}
